import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Collapse,
  Drawer,
  Group,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconMessages,
  IconPlayerPlay,
  IconRefresh,
} from '@tabler/icons-react';
import { useEffect, useId, useState } from 'react';
import { MAX_PLAYBOOK_LENGTH, playbookLength } from '../../helpers/agentFieldMapping';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import HelpPopover from '../HelpPopover/HelpPopover';
import { ScreenHint, formatDate } from '../KnowledgeBase/shared';
import ConfirmationModal from '../Modals/ConfirmationModal';
import Spinner from '../Spinner';
import { AssistantMessage, ShopperMessage, messageKey } from './ConversationDetail';
import { HELP } from './superAdmin/superAdminHelp';

const SEGMENTS = [
  { value: 'pending', label: 'Pending' },
  { value: 'applied', label: 'Applied' },
  { value: 'dismissed', label: 'Dismissed' },
];

const sourceLabel = (suggestion) => {
  if (suggestion.source === 'feedback') return `👎 ${suggestion.evidence?.length ?? 0} answers (AI)`;
  if (suggestion.kind === 'promote') return '★ Promoted';
  return suggestion.refineVerdict === 'rewritten' ? '✎ edited · AI rewrote' : '✎ Edited by user';
};

const TARGET_LABEL = { playbook: 'Playbook', knowledge: 'Knowledge' };

// What the client sent before Run now rewrote it, collapsed under the AI version.
const ClientOriginal = ({ original, target }) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const text = original.target === 'playbook' ? original.change : original.proposed?.answer;
  return (
    <Box>
      <Button
        variant="subtle"
        size="compact-xs"
        color="gray"
        leftSection={open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(!open)}
      >
        Client wrote
      </Button>
      <Collapse id={panelId} expanded={open}>
        <Stack gap={4} pl="sm" pt={4}>
          <Text size="xs" c="dimmed">
            {original.target === target
              ? `Client picked ${TARGET_LABEL[original.target]}`
              : `Client picked ${TARGET_LABEL[original.target]} → AI moved it to ${TARGET_LABEL[target]}`}
          </Text>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {text}
          </Text>
        </Stack>
      </Collapse>
    </Box>
  );
};

const PlaybookBox = ({ title, text, children }) => {
  const length = playbookLength(text);
  return (
    <Card withBorder padding="sm" component="section" aria-label={title}>
      <Group justify="space-between" mb="xs">
        <Title order={6}>{title}</Title>
        <Text size="xs" c={length > MAX_PLAYBOOK_LENGTH ? 'red' : 'dimmed'}>
          {length} / {MAX_PLAYBOOK_LENGTH}
        </Text>
      </Group>
      {children || (
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} c={text ? undefined : 'dimmed'}>
          {text || 'Empty'}
        </Text>
      )}
    </Card>
  );
};

// Every session the suggestion points at, with the referenced replies outlined.
const EvidenceDrawer = ({ fetchUrl, evidence, onClose }) => {
  const fetch = useFetch();
  const [sessions, setSessions] = useState(null);
  const highlighted = new Set(evidence.map(({ messageId }) => messageId));

  useEffect(() => {
    let cancelled = false;
    const ids = [...new Set(evidence.map(({ chatSessionId }) => chatSessionId))];
    Promise.all(
      ids.map((id) =>
        fetch(`${fetchUrl}/chatBot/sessions/${encodeURIComponent(id)}`)
          .then(({ session }) => session || { chatSessionId: id, missing: true })
          .catch(() => ({ chatSessionId: id, missing: true }))
      )
    ).then((loaded) => {
      if (!cancelled) setSessions(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [fetch, fetchUrl, evidence]);

  return (
    <Drawer opened onClose={onClose} position="right" size="xl" title="Conversations">
      {sessions === null ? (
        <Spinner />
      ) : (
        <Stack gap="xl">
          {sessions.map((session) => (
            <Stack key={session.chatSessionId} gap="md">
              <Text size="xs" c="dimmed">
                {session.chatSessionId}
                {session.startedAt && ` · started ${formatDate(session.startedAt)}`}
              </Text>
              {session.missing ? (
                <ErrorMessage message="Could not load this conversation." />
              ) : (
                (session.messages || []).map((message, index) =>
                  message.sender === 'shopper' ? (
                    <ShopperMessage key={messageKey(message, index)} message={message} />
                  ) : (
                    <AssistantMessage
                      key={messageKey(message, index)}
                      message={message}
                      highlighted={highlighted.has(message.messageId)}
                    />
                  )
                )
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </Drawer>
  );
};

const SuggestionCard = ({ suggestion, learning, editing, setEditing, busy, onApprove, onDismiss, onEvidence }) => {
  const { status, target, proposed, stale } = suggestion;
  const isPlaybook = target === 'playbook';
  const isEditing = editing?.id === suggestion.id;
  const draft = isEditing ? editing : null;
  const playbookText = draft ? draft.playbook : proposed.playbook;
  const overCap = isPlaybook && playbookLength(playbookText) > MAX_PLAYBOOK_LENGTH;
  const blocked = isPlaybook && !learning.playbookSupported;
  const evidenceCount = suggestion.evidence?.length || 0;
  const isCorrection = suggestion.source === 'manual' && suggestion.kind === 'fix';

  // A stale playbook item is edited onto the CURRENT playbook, so the save is checked against that.
  const startEdit = () =>
    setEditing(
      isPlaybook
        ? {
            id: suggestion.id,
            playbook: stale
              ? [learning.agentPlaybook, suggestion.change].filter(Boolean).join('\n')
              : proposed.playbook || '',
            baseHash: stale ? learning.agentPlaybookHash : suggestion.baseHash,
          }
        : { id: suggestion.id, question: proposed.question, answer: proposed.answer }
    );

  const patch = (changes) => setEditing({ ...editing, ...changes });
  const editBody = () =>
    isPlaybook
      ? { playbook: draft.playbook, baseHash: draft.baseHash }
      : { question: draft.question, answer: draft.answer };
  const draftEmpty =
    draft && (isPlaybook ? !draft.playbook?.trim() : !draft.question?.trim() || !draft.answer?.trim());

  return (
    <Card withBorder radius="sm" padding="md">
      <Card.Section inheritPadding withBorder py="sm">
        <Group gap={8} wrap="wrap">
          <Text fw={600} size="sm">
            {suggestion.title}
          </Text>
          <Badge variant="light" color={isPlaybook ? 'violet' : 'teal'} size="sm" tt="none">
            {isPlaybook ? 'Playbook' : 'Knowledge'}
          </Badge>
          <Badge variant="default" size="sm" tt="none">
            {sourceLabel(suggestion)}
          </Badge>
          {isCorrection && suggestion.refineVerdict === 'covered' && (
            <Badge variant="light" color="gray" size="sm" tt="none">
              AI: already covered
            </Badge>
          )}
          {evidenceCount > 0 && (
            <Button variant="subtle" size="compact-xs" ml="auto" onClick={onEvidence}>
              View conversations
            </Button>
          )}
        </Group>
      </Card.Section>

      <Stack gap={8} pt={12}>
        {suggestion.window && (
          <Text size="xs" c="dimmed">
            From {evidenceCount} down-voted answers, {formatDate(suggestion.window.from)}–
            {formatDate(suggestion.window.to)}
          </Text>
        )}
        {suggestion.rationale && <Text size="sm">{suggestion.rationale}</Text>}
        {suggestion.note && (
          <Text size="sm" c="dimmed">
            Note: {suggestion.note}
          </Text>
        )}
        {status === 'pending' && isCorrection && !suggestion.refineVerdict && (
          <Text size="xs" c="dimmed">
            Run now will rewrite this in the store&apos;s language.
          </Text>
        )}
        {status === 'pending' && stale && (
          <Alert color="warning" variant="light" icon={<IconAlertTriangle size={16} />} p="xs">
            The playbook changed since this was suggested. Edit to add the change to the current playbook.
          </Alert>
        )}

        {isPlaybook ? (
          <>
            {suggestion.change && (
              <Text size="sm">
                <Text span fw={600}>
                  What changed:
                </Text>{' '}
                {suggestion.change}
              </Text>
            )}
            {status === 'pending' && (
              <SimpleGrid cols={{ base: 1, md: 2 }}>
                <PlaybookBox title="Current playbook" text={learning.agentPlaybook} />
                <PlaybookBox title="Proposed" text={playbookText}>
                  {draft && (
                    <Textarea
                      aria-label="Proposed playbook"
                      autosize
                      minRows={4}
                      value={draft.playbook}
                      onChange={(e) => patch({ playbook: e.currentTarget.value })}
                    />
                  )}
                </PlaybookBox>
              </SimpleGrid>
            )}
          </>
        ) : draft ? (
          <>
            <Textarea
              label="Question"
              autosize
              minRows={1}
              maxLength={500}
              value={draft.question}
              onChange={(e) => patch({ question: e.currentTarget.value })}
            />
            <Textarea
              label="Answer"
              autosize
              minRows={3}
              maxLength={4000}
              value={draft.answer}
              onChange={(e) => patch({ answer: e.currentTarget.value })}
            />
          </>
        ) : (
          <Box>
            <Text size="sm" fw={600}>
              {proposed.question}
            </Text>
            <Text fz={13} c="gray.7" lh={1.55} style={{ whiteSpace: 'pre-wrap' }}>
              {proposed.answer}
            </Text>
          </Box>
        )}

        {suggestion.refineVerdict === 'rewritten' && suggestion.original && (
          <ClientOriginal original={suggestion.original} target={target} />
        )}

        {status === 'pending' ? (
          <Group justify="flex-end" gap={8} mt={4}>
            {draft ? (
              <>
                <Button size="xs" variant="default" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button
                  size="xs"
                  color="success"
                  loading={busy}
                  disabled={overCap || blocked || draftEmpty}
                  onClick={() => onApprove(editBody())}
                >
                  Save & approve
                </Button>
              </>
            ) : (
              <>
                <Button size="xs" variant="subtle" color="danger" onClick={onDismiss}>
                  Dismiss
                </Button>
                <Button size="xs" variant="default" onClick={startEdit}>
                  Edit
                </Button>
                <Button
                  size="xs"
                  color="success"
                  leftSection={<IconCheck size={13} stroke={2} />}
                  loading={busy}
                  disabled={overCap || blocked || stale}
                  onClick={() => onApprove({})}
                >
                  Approve
                </Button>
              </>
            )}
          </Group>
        ) : (
          <Text size="xs" c="dimmed">
            {status === 'applied' ? 'Approved' : 'Dismissed'} {formatDate(suggestion.decidedAt)}
            {suggestion.decidedBy != null && ` by user #${suggestion.decidedBy}`}
          </Text>
        )}
      </Stack>
    </Card>
  );
};

const EmptyPending = ({ learning, running, runDisabled, onRun }) => {
  const { suggestions, newDownVotes, lastRunAt, lastRunReviewed } = learning;
  // A run that found something leaves a feedback item behind, even once it is decided.
  const lastRunFoundNothing =
    lastRunAt && lastRunReviewed > 0 && !suggestions.some((s) => s.source === 'feedback' && new Date(s.createdAt) >= new Date(lastRunAt));

  let body;
  if (newDownVotes > 0) {
    body = (
      <>
        <Text size="sm" c="dimmed">
          {newDownVotes} new 👎 {lastRunAt ? 'since the last run' : 'in the last 14 days'}.
        </Text>
        <Button
          size="xs"
          leftSection={<IconPlayerPlay size={14} />}
          loading={running}
          disabled={runDisabled}
          onClick={onRun}
        >
          Run now
        </Button>
      </>
    );
  } else if (lastRunFoundNothing) {
    body = (
      <Text size="sm" c="dimmed">
        Reviewed {lastRunReviewed} down-voted answers; none needed a change.
      </Text>
    );
  } else if (suggestions.length === 0) {
    body = (
      <Text size="sm" c="dimmed" maw={520}>
        Shoppers rate answers with 👍 and 👎. In Conversations, anyone on this store can rewrite an answer with ✎ or
        keep a good one with ★. Down-voted answers, edits and promoted answers end up here for review.
      </Text>
    );
  } else {
    body = (
      <Text size="sm" c="dimmed">
        Nothing waiting for review.
      </Text>
    );
  }

  return (
    <Center py={56}>
      <Stack align="center" gap={8} ta="center">
        <IconMessages size={44} stroke={1} color="var(--mantine-color-gray-5)" />
        {body}
      </Stack>
    </Center>
  );
};

const latest = (...values) =>
  values.filter(Boolean).reduce((max, value) => (!max || new Date(value) > new Date(max) ? value : max), null);

const LearningTab = ({ fetchUrl, onPendingCount, onPlaybookApplied }) => {
  const fetch = useFetch();
  const base = `${fetchUrl}/chatBot/learning`;

  const [learning, setLearning] = useState();
  const [loadError, setLoadError] = useState();
  const [reloads, setReloads] = useState(0);
  const [segment, setSegment] = useState('pending');
  const [running, setRunning] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [dismissTarget, setDismissTarget] = useState(null);
  const [note, setNote] = useState('');
  const [dismissing, setDismissing] = useState(false);
  const [dismissError, setDismissError] = useState();
  const [evidenceFor, setEvidenceFor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(base)
      .then((data) => {
        if (cancelled) return;
        setLearning(data);
        setLoadError(undefined);
        onPendingCount?.(data.suggestions.filter(({ status }) => status === 'pending').length);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error);
      });
    return () => {
      cancelled = true;
    };
  }, [fetch, base, reloads, onPendingCount]);

  const reload = () => setReloads((count) => count + 1);

  // The 202 has no body through useFetch, so the reload is what brings the new cooldown.
  const runNow = () => {
    setRunning(true);
    fetch(`${base}/run`, { method: 'POST' })
      .then(() => {
        notifications.show({
          message: 'Learning started — suggestions appear in a minute or two. Press Refresh to see them.',
          color: 'success',
        });
        reload();
      })
      .catch((error) => {
        if (error?.nextRunAllowedAt) setBlockedUntil(error.nextRunAllowedAt);
        notifications.show({ title: 'Run failed', message: getErrorMessage(error), color: 'danger' });
      })
      .finally(() => setRunning(false));
  };

  const approve = (suggestion, body) => {
    setBusyId(suggestion.id);
    fetch(`${base}/${suggestion.id}/apply`, { method: 'POST', body: JSON.stringify(body) })
      .then((response) => {
        notifications.show({
          message: suggestion.target === 'playbook' ? 'The playbook is updated.' : 'Published to the knowledge base.',
          color: 'success',
        });
        if (suggestion.target === 'playbook') {
          onPlaybookApplied?.({
            agentPlaybook: response.agentPlaybook,
            agentPlaybookHash: response.agentPlaybookHash,
            learningLastApplied: { title: suggestion.title, at: response.suggestion?.decidedAt },
          });
        }
        setEditing(null);
        reload();
      })
      .catch((error) => {
        notifications.show({ title: 'Approve failed', message: getErrorMessage(error), color: 'danger' });
        // A stale playbook 409 carries the current playbook: reload so the card shows it.
        if (error?.agentPlaybookHash) reload();
      })
      .finally(() => setBusyId(null));
  };

  const closeDismiss = () => {
    setDismissTarget(null);
    setNote('');
    setDismissError(undefined);
  };

  const dismiss = (close) => {
    setDismissing(true);
    setDismissError(undefined);
    fetch(`${base}/${dismissTarget.id}/dismiss`, {
      method: 'POST',
      body: JSON.stringify(note.trim() ? { note: note.trim() } : {}),
    })
      .then(() => {
        close();
        reload();
      })
      .catch((error) => setDismissError({ message: getErrorMessage(error) }))
      .finally(() => setDismissing(false));
  };

  if (loadError) return <ErrorMessage message={getErrorMessage(loadError)} />;
  if (!learning) return <Spinner />;

  const { suggestions, newDownVotes, correctionsToRefine, lastRunAt, lastRunReviewed, lastRunRefined } = learning;
  const counts = Object.fromEntries(
    SEGMENTS.map(({ value }) => [value, suggestions.filter(({ status }) => status === value).length])
  );
  const shown = suggestions.filter(({ status }) => status === segment);
  const nextRunAt = latest(learning.nextRunAllowedAt, blockedUntil);
  const runDisabled = Boolean(nextRunAt && new Date(nextRunAt) > new Date());

  return (
    <Box mt="lg">
      <Group justify="space-between" wrap="wrap" gap={8}>
        <Group gap={8} wrap="nowrap">
          <ScreenHint text="Suggestions to improve this agent. Nothing changes until a super admin approves an item." />
          <HelpPopover {...HELP.learning} />
          <Text size="sm" c="dimmed">
            {newDownVotes} new 👎 {lastRunAt ? 'since the last run' : 'in the last 14 days'} ·{' '}
            {correctionsToRefine > 0 &&
              `${correctionsToRefine} client ${correctionsToRefine === 1 ? 'correction' : 'corrections'} to rewrite · `}
            {lastRunAt
              ? `Last run ${formatDate(lastRunAt)} (reviewed ${lastRunReviewed}${
                  lastRunRefined > 0 ? `, refined ${lastRunRefined}` : ''
                })`
              : 'Never run'}
          </Text>
        </Group>
        <Group gap={8} wrap="nowrap">
          <HelpPopover {...HELP.learningEditPromote} />
          <Button size="xs" variant="default" leftSection={<IconRefresh size={14} />} onClick={reload}>
            Refresh
          </Button>
          <Button
            size="xs"
            leftSection={<IconPlayerPlay size={14} />}
            loading={running}
            disabled={runDisabled}
            onClick={runNow}
          >
            {runDisabled ? `Available at ${formatDate(nextRunAt)}` : 'Run now'}
          </Button>
          <HelpPopover {...HELP.learningRunNow} />
        </Group>
      </Group>

      {!learning.playbookSupported && (
        <Alert color="warning" variant="light" mt="sm" icon={<IconAlertTriangle size={16} />}>
          This store&apos;s agent profile does not use the playbook, so playbook items can&apos;t be approved.
        </Alert>
      )}
      {!learning.knowledgeSearchAvailable && (
        <Alert color="warning" variant="light" mt="sm" icon={<IconAlertTriangle size={16} />}>
          Knowledge search is not available on this store&apos;s agent profile, so approved knowledge items won&apos;t
          be used by the agent.
        </Alert>
      )}

      <SegmentedControl
        mt="md"
        aria-label="Learning filter"
        value={segment}
        onChange={(value) => {
          setSegment(value);
          setEditing(null);
        }}
        data={SEGMENTS.map(({ value, label }) => ({ value, label: `${label} · ${counts[value]}` }))}
      />

      {shown.length === 0 ? (
        segment === 'pending' ? (
          <EmptyPending learning={learning} running={running} runDisabled={runDisabled} onRun={runNow} />
        ) : (
          <Text size="sm" c="dimmed" ta="center" py={56}>
            Nothing here yet.
          </Text>
        )
      ) : (
        <Stack gap={10} mt={14}>
          {shown.map((suggestion) => (
            <SuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              learning={learning}
              editing={editing}
              setEditing={setEditing}
              busy={busyId === suggestion.id}
              onApprove={(body) => approve(suggestion, body)}
              onDismiss={() => setDismissTarget(suggestion)}
              onEvidence={() => setEvidenceFor(suggestion)}
            />
          ))}
        </Stack>
      )}

      <ConfirmationModal
        show={dismissTarget !== null}
        close={closeDismiss}
        confirm={dismiss}
        loading={dismissing}
        error={dismissError}
        message={dismissTarget && `This dismisses “${dismissTarget.title}”. Nothing is changed.`}
      >
        <Textarea
          label="Note"
          description="Optional."
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          autosize
          minRows={2}
        />
      </ConfirmationModal>

      {evidenceFor && (
        <EvidenceDrawer
          key={evidenceFor.id}
          fetchUrl={fetchUrl}
          evidence={evidenceFor.evidence}
          onClose={() => setEvidenceFor(null)}
        />
      )}
    </Box>
  );
};

export default LearningTab;
